import {
  getGetSensorAggregatesQueryOptions,
  useListSensors,
  type SensorsSensor,
} from '@smarthome/api';
import { useQueries } from '@tanstack/react-query';
import { AlertTriangle, BarChart3, Check, ChevronDown, Expand, X } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';
import { useDashboardTimeRange } from '../viewer/DashboardTimeRangeContext';

import { HistogramChart, Modal, type HistogramDataset } from '@/components/ui';

/**
 * Time range options for the histogram
 */
type TimeRange = '24h' | '7d' | '30d';
type BucketSize = '1h' | '1d';

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

const BUCKET_SIZE_OPTIONS: { value: BucketSize; label: string }[] = [
  { value: '1h', label: '1 hour' },
  { value: '1d', label: '1 day' },
];

const TIME_RANGE_MS: Record<TimeRange, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

/**
 * Histogram widget config schema
 */
export const histogramConfigSchema = z.object({
  sensorHumanIds: z.array(z.string()).min(1, 'At least one sensor is required'),
  timeRangeMode: z.enum(['dashboard', 'fixed']).default('dashboard'),
  timeRange: z.enum(['24h', '7d', '30d']).default('7d'),
  bucketSize: z.enum(['1h', '1d']).default('1h'),
  /** How to display multiple sensors: stacked bars or summed into one */
  displayMode: z.enum(['stacked', 'summed']).default('stacked'),
});

export type HistogramConfig = z.infer<typeof histogramConfigSchema>;
type DisplayMode = 'stacked' | 'summed';

const DISPLAY_MODE_OPTIONS: { value: DisplayMode; label: string }[] = [
  { value: 'stacked', label: 'Stacked bars' },
  { value: 'summed', label: 'Combined total' },
];

/**
 * Multi-sensor selector component
 */
function SensorMultiSelect({
  selectedIds,
  onChange,
  error,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  error?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: sensorsData, isLoading } = useListSensors();
  const sensors = sensorsData?.sensors ?? [];

  const selectedSensors = sensors.filter((s) => s.humanId && selectedIds.includes(s.humanId));

  const handleToggle = (sensor: SensorsSensor) => {
    if (!sensor.humanId) return;
    if (selectedIds.includes(sensor.humanId)) {
      onChange(selectedIds.filter((id) => id !== sensor.humanId));
    } else {
      onChange([...selectedIds, sensor.humanId]);
    }
  };

  const handleRemove = (id: string) => {
    onChange(selectedIds.filter((i) => i !== id));
  };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        Sensors
      </label>

      {/* Selected sensors chips */}
      {selectedSensors.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selectedSensors.map((sensor) => (
            <span
              key={sensor.humanId}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
            >
              {sensor.name || sensor.humanId}
              <button
                type="button"
                onClick={() => sensor.humanId && handleRemove(sensor.humanId)}
                className="rounded-full p-0.5 hover:bg-blue-200 dark:hover:bg-blue-500/30"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
            error
              ? 'border-red-300 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10'
              : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-white/[0.12]'
          }`}
        >
          <span className="text-gray-500 dark:text-zinc-500">
            {selectedSensors.length > 0
              ? `${selectedSensors.length} sensor${selectedSensors.length > 1 ? 's' : ''} selected`
              : 'Select sensors...'}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                  Loading sensors...
                </div>
              ) : sensors.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                  No sensors available
                </div>
              ) : (
                sensors.map((sensor) => (
                  <button
                    key={sensor.id}
                    type="button"
                    onClick={() => handleToggle(sensor)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                  >
                    <div>
                      <div className="text-gray-900 dark:text-white">
                        {sensor.name || sensor.humanId}
                      </div>
                      {sensor.name && sensor.humanId && (
                        <div className="text-xs text-gray-500 dark:text-zinc-500">
                          {sensor.humanId}
                        </div>
                      )}
                    </div>
                    {sensor.humanId && selectedIds.includes(sensor.humanId) && (
                      <Check className="h-4 w-4 text-blue-500" />
                    )}
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Config form for Histogram widget
 */
function HistogramConfigForm({ config, onChange, errors }: ConfigFormProps<HistogramConfig>) {
  const timeRangeMode = config.timeRangeMode ?? 'fixed';
  return (
    <div className="space-y-4">
      <SensorMultiSelect
        selectedIds={config.sensorHumanIds}
        onChange={(ids) => onChange({ ...config, sensorHumanIds: ids })}
        error={errors?.['sensorHumanIds']}
      />

      {/* Time range mode selector */}
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Time Range
        </label>
        <select
          value={timeRangeMode}
          onChange={(e) =>
            onChange({
              ...config,
              timeRangeMode: e.target.value as 'dashboard' | 'fixed',
            })
          }
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
        >
          <option value="dashboard">Use dashboard timeframe</option>
          <option value="fixed">Fixed timeframe</option>
        </select>
      </div>

      {/* Fixed time range selector — only shown in fixed mode */}
      {timeRangeMode === 'fixed' && (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Fixed Time Range
          </label>
          <select
            value={config.timeRange}
            onChange={(e) => onChange({ ...config, timeRange: e.target.value as TimeRange })}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
          >
            {TIME_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Bucket size selector */}
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Bucket Size
        </label>
        <select
          value={config.bucketSize}
          onChange={(e) => onChange({ ...config, bucketSize: e.target.value as BucketSize })}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
        >
          {BUCKET_SIZE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Display mode selector (only show when multiple sensors) */}
      {config.sensorHumanIds.length > 1 && (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Display Mode
          </label>
          <select
            value={config.displayMode ?? 'stacked'}
            onChange={(e) => onChange({ ...config, displayMode: e.target.value as DisplayMode })}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
          >
            {DISPLAY_MODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * Renderer for Histogram widget
 */
const HistogramRenderer = memo(function HistogramRenderer({
  config,
}: RendererProps<HistogramConfig>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const dashboardTimeRange = useDashboardTimeRange();

  // Use dashboard time range when mode is 'dashboard', otherwise use fixed config
  // Histogram only supports '24h', '7d', '30d' — clamp dashboard values
  const timeRangeMode = config.timeRangeMode ?? 'fixed';
  const effectiveTimeRange: TimeRange =
    timeRangeMode === 'dashboard'
      ? dashboardTimeRange in TIME_RANGE_MS
        ? (dashboardTimeRange as TimeRange)
        : '24h'
      : config.timeRange;

  // Memoize time range to prevent infinite refetch loops
  // Truncate to seconds to ensure consistent API queries across sensors
  const { startTime, endTime } = useMemo(() => {
    const nowMs = Math.floor(Date.now() / 1000) * 1000;
    const rangeDuration = TIME_RANGE_MS[effectiveTimeRange];
    return {
      startTime: new Date(nowMs - rangeDuration).toISOString(),
      endTime: new Date(nowMs).toISOString(),
    };
  }, [effectiveTimeRange]);

  // Fetch aggregates for ALL configured sensors in parallel
  const sensorQueries = useQueries({
    queries: config.sensorHumanIds.map((sensorId) =>
      getGetSensorAggregatesQueryOptions(
        sensorId,
        {
          start: startTime,
          end: endTime,
          interval: config.bucketSize,
          aggregation: 'delta',
        },
        {
          query: {
            enabled: !!sensorId,
            refetchInterval: 60000,
          },
        }
      )
    ),
  });

  const isLoading = sensorQueries.some((q) => q.isLoading);
  const hasError = sensorQueries.some((q) => q.isError);

  // Fetch sensor metadata for labels
  const { data: sensorsData } = useListSensors();
  const sensorsMap = useMemo(() => {
    const map = new Map<string, SensorsSensor>();
    for (const sensor of sensorsData?.sensors ?? []) {
      if (sensor.humanId) {
        map.set(sensor.humanId, sensor);
      }
    }
    return map;
  }, [sensorsData?.sensors]);

  const displayMode = config.displayMode ?? 'stacked';
  const useStackedMode = displayMode === 'stacked' && config.sensorHumanIds.length > 1;

  // Create separate datasets for stacked mode
  const chartDatasets: HistogramDataset[] = useMemo(() => {
    if (!useStackedMode) return [];

    return config.sensorHumanIds.map((sensorId, index) => {
      const query = sensorQueries[index];
      const sensor = sensorsMap.get(sensorId);
      const aggregates = query?.data?.aggregates ?? [];

      return {
        label: sensor?.name || sensorId,
        color: sensor?.unitColor || undefined,
        data: aggregates.map((agg) => ({
          timestamp: agg.timestamp ?? '',
          value: agg.value ?? null,
        })),
      };
    });
  }, [config.sensorHumanIds, sensorQueries, sensorsMap, useStackedMode]);

  // Summed data for combined mode (single dataset)
  const chartDataSummed = useMemo(() => {
    if (useStackedMode) return [];

    const dataByTimestamp = new Map<string, number>();

    for (const query of sensorQueries) {
      const aggregates = query.data?.aggregates;
      if (!aggregates) continue;

      for (const agg of aggregates) {
        if (!agg.timestamp) continue;
        const existing = dataByTimestamp.get(agg.timestamp) ?? 0;
        dataByTimestamp.set(agg.timestamp, existing + (agg.value ?? 0));
      }
    }

    // Convert to sorted array
    return Array.from(dataByTimestamp.entries())
      .map(([timestamp, value]) => ({ timestamp, value }))
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  }, [sensorQueries, useStackedMode]);

  if (config.sensorHumanIds.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No sensors configured</p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">Failed to load sensor data</p>
      </div>
    );
  }

  const chartContent = (
    <HistogramChart
      data={chartDataSummed}
      datasets={useStackedMode ? chartDatasets : undefined}
      stacked={useStackedMode}
      bucketSize={config.bucketSize}
      startTime={startTime}
      endTime={endTime}
      height={undefined}
      isLoading={isLoading}
    />
  );

  return (
    <>
      <div
        className="group relative flex h-full cursor-pointer flex-col"
        onClick={() => setIsExpanded(true)}
      >
        {/* Expand hint */}
        <div className="absolute right-2 top-2 z-10 rounded-md bg-black/50 p-1 opacity-0 transition-opacity group-hover:opacity-100">
          <Expand className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="min-h-0 flex-1">{chartContent}</div>
      </div>

      {/* Fullscreen modal */}
      <Modal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        title="Histogram"
        subtitle={`${config.sensorHumanIds.length} sensor${config.sensorHumanIds.length > 1 ? 's' : ''} · ${effectiveTimeRange} · ${config.bucketSize} buckets`}
        size="xl"
      >
        <div className="h-[60vh]">
          <HistogramChart
            data={chartDataSummed}
            datasets={useStackedMode ? chartDatasets : undefined}
            stacked={useStackedMode}
            bucketSize={config.bucketSize}
            startTime={startTime}
            endTime={endTime}
            height={undefined}
            isLoading={isLoading}
          />
        </div>
      </Modal>
    </>
  );
});

/**
 * Histogram widget definition
 */
export const histogramWidget: WidgetDefinition<HistogramConfig> = {
  type: 'histogram',
  displayName: 'Histogram',
  description: 'Display sensor data as a histogram chart',
  icon: BarChart3,
  defaultSize: { w: 4, h: 3 },
  minSize: { w: 3, h: 2 },
  maxSize: { w: 12, h: 6 },
  configSchema: histogramConfigSchema,
  defaultConfig: {
    sensorHumanIds: [],
    timeRangeMode: 'dashboard',
    timeRange: '7d',
    bucketSize: '1h',
    displayMode: 'stacked',
  },
  ConfigForm: HistogramConfigForm,
  Renderer: HistogramRenderer,
  supportsTimeRange: true,
};
